import { useState } from 'react';
import { keywordAdditions, keywordKey, keywordValues, newKeywordStyle } from '../utils/keywordAdditions';
import { Badge, IdChip, text } from './CrimtanOptimizationBreakdown';
import {
  impactCell, impactText, directionColor, directionArrow,
} from '../utils/changeImpactPresentation';
import { hideMicros } from '../utils/optimizerResultPresentation';

/**
 * A focused optimizer run's actionable rows, as a table.
 *
 * The suggestion cards render one recommendation per card, which reads well when each
 * one is a paragraph and badly when the question is "what is changing, where, and can I
 * click it?" — the comparison a trader actually makes. Worse on DV360: the cards name
 * the LINE ITEM and never the insertion order, so a run covering seven IOs produced a
 * column of cards with no way to tell which goal each one served.
 *
 * The columns are the shape of the decision:
 *
 *   1. Scope         which insertion order (DV360) or campaign (Search / PMax)
 *   2. Targets       the line items / ad groups / asset groups it changes
 *   3. Current       what the setting is now, or what the diagnostic found
 *   4. Recommended   what to change it to, or the fix that clears the blocker
 *   5. Notes         why, with the math or the evidence behind it
 *   6. Action        the Apply button, or where to make the change by hand
 *   7. Actual Impact  for a row already applied, what delivery, CTR and conversion
 *                     rate did afterwards — measured, not predicted
 *
 * WHY THE SAME TABLE FOR ALL THREE ANALYSES
 * It started as pacing only, on the theory that diagnostic and performance rows carry
 * narrative the columns would truncate. They do not: a diagnostic row is "these four
 * line items under this IO have no creatives — assign them", and a performance row is
 * "these three lines need this domain list". Both answer the table's six questions, and
 * both were previously rendered as a stack of cards that repeated the same sentence once
 * per line item and never named the insertion order. What each analysis calls its
 * columns differs — a diagnostic has a FIX where a pacing run has a recommended value —
 * so the two middle headings follow `kind`.
 *
 * WHY THE SAME TABLE ON THREE PLATFORMS
 * DV360 is the only family with a real two-level hierarchy: many insertion orders per
 * campaign, each recommendation touching several line items. Search and PMax pace ONE
 * campaign, so their scope column carries the campaign and their target column a single
 * ad group or asset group. The columns are the same because the question is; what
 * differs is only which entity fills them, which `resolveRow` decides from the platform.
 *
 * Apply is NOT reimplemented here. `renderAction` comes from the activity feed, which
 * owns the applying/applied state keyed on the backend's original suggestion index —
 * the same reason the cards were left alone when the focused breakdowns were added.
 */

const cell = {
  padding: '10px 12px', verticalAlign: 'top', borderBottom: '1px solid #f3f4f6',
  fontSize: '0.85em', color: '#374151',
};

const headerCell = {
  ...cell, borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
  fontSize: '0.72em', fontWeight: 700, letterSpacing: '0.4px',
  textTransform: 'uppercase', color: '#6b7280', whiteSpace: 'nowrap',
};

const linkButton = {
  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
  color: '#0F7B5F', fontSize: '0.95em', fontWeight: 600,
};

/** How many list entries and how much prose a cell shows before it asks to be expanded. */
const VALUES_SHOWN = 5;
const NOTE_CLAMP_CHARS = 150;

/**
 * A value cell's content as a list, when it is one.
 *
 * A list lever is asked for a JSON array in `recommended_value`, and runs variously send
 * the array, the array serialized into a string, or a comma-joined line. All three are
 * the same thing to a reader, so all three render as chips rather than one of them
 * rendering as a wall of text with brackets in it.
 */
export function asValueList(value) {
  if (Array.isArray(value)) {
    const entries = value.map(text).filter(Boolean);
    return entries.length ? entries : null;
  }
  const raw = typeof value === 'string' ? value.trim() : null;
  if (!raw) return null;
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const entries = parsed.map(text).filter(Boolean);
        return entries.length ? entries : null;
      }
    } catch {
      // Not JSON after all — fall through to the comma split below.
    }
  }
  // Only split on commas when the parts look like values rather than a sentence with
  // clauses in it. Two words per part is the bar: "Luxury Resorts" and "afar.com" pass,
  // "raise the cap, because the IO is short" does not.
  if (raw.includes(',')) {
    const parts = raw.split(/[,\n]+/).map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1 && parts.every((p) => p.split(/\s+/).length <= 3)) return parts;
  }
  return null;
}

/**
 * Pair a list of values with the names the run resolved for them.
 *
 * An audience or PMP lever's values are ids, and an id is unreadable: "24135209" says
 * nothing about which publisher a trader is being asked to buy, and the deal name is the
 * whole basis the recommendation was made on. The run resolves those names in code and
 * sends them as a parallel array; this is where they become visible.
 *
 * Paired BY INDEX, and only when the two arrays line up exactly — a mismatched pairing
 * would label one deal with another deal's name, which is worse than showing the id.
 * `asValueList` can re-parse a value that arrived as a string, so the lengths really can
 * disagree.
 */
export function pairNames(entries, names) {
  if (!entries) return null;
  const resolved = Array.isArray(names) ? names.map(text) : [];
  if (resolved.length !== entries.length) return entries.map((value) => ({ value }));
  return entries.map((value, i) => ({
    value,
    // A name equal to the value is the resolver saying it could not name that id — it
    // fills the slot with the id itself so the two arrays stay aligned.
    name: resolved[i] && resolved[i] !== value ? resolved[i] : undefined,
  }));
}

/** Chips for a list of values, past `VALUES_SHOWN` behind a toggle. */
function ValueList({ entries, muted, additions }) {
  const [open, setOpen] = useState(false);
  // Accepts bare values as well as {value, name} pairs: the "not applied" list below has
  // no names to show.
  const pairs = entries.map((e) => (e && typeof e === 'object' ? e : { value: e }));
  const hidden = pairs.length - VALUES_SHOWN;
  const shown = open ? pairs : pairs.slice(0, VALUES_SHOWN);
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {shown.map((entry, i) => (
          <code
            key={`${entry.value}-${i}`}
            // The id stays reachable on hover, so a reader checking the payload or
            // pasting into DV360 does not have to go looking for it.
            title={entry.name ? entry.value : undefined}
            style={{
              background: muted ? '#fef2f2' : '#f3f4f6',
              color: muted ? '#991b1b' : '#374151',
              padding: '1px 6px', borderRadius: '3px', fontSize: '0.9em',
              fontFamily: 'monospace',
              textDecoration: muted ? 'line-through' : 'none',
              ...(!muted && additions?.has(keywordKey(entry.value)) ? newKeywordStyle : {}),
            }}
          >{entry.name || entry.value}{!muted && additions?.has(keywordKey(entry.value)) && <span> · New</span>}</code>
        ))}
      </div>
      {hidden > 0 && (
        <button type="button" style={{ ...linkButton, marginTop: '4px' }}
                onClick={() => setOpen((v) => !v)}>
          {open ? 'show fewer' : `+${hidden} more`}
        </button>
      )}
    </div>
  );
}

/** Prose past `NOTE_CLAMP_CHARS` behind a toggle, so one long rationale cannot set the row height. */
function ClampedText({ children }) {
  const [open, setOpen] = useState(false);
  const value = text(children);
  if (!value) return null;
  if (value.length <= NOTE_CLAMP_CHARS) return <span>{value}</span>;
  return (
    <span>
      {open ? value : `${value.slice(0, NOTE_CLAMP_CHARS).trimEnd()}…`}{' '}
      <button type="button" style={linkButton} onClick={() => setOpen((v) => !v)}>
        {open ? 'less' : 'more'}
      </button>
    </span>
  );
}

/** A value cell: a chip list when the value is a list, clamped prose otherwise.
 *
 * Micros are hidden here rather than upstream: DV360 stores money in millionths, and a
 * run that writes "9.00 (9000000 micros)" is saying the same number twice in a column
 * that then needs twice the width. Display only — the value the apply path sends is
 * built from the payload, not from this cell.
 */
function ValueCell({ value, skipped, names, additions }) {
  const dropped = (Array.isArray(skipped) ? skipped : [])
    .map((s) => text(s && typeof s === 'object' ? s.value : s))
    .filter(Boolean);
  // The recommended list still contains the entries the apply path refused. Showing it
  // whole and then repeating the refused ones underneath prints them twice and leaves
  // the reader to diff two lists; the top list is what WILL be sent, and only that.
  //
  // Names are paired BEFORE the filter and the micros pass, both of which would
  // otherwise shift every name onto the wrong id.
  const all = pairNames(additions ? keywordValues(value) : asValueList(value), names);
  const filtered = all && dropped.length
    ? all.filter((e) => !dropped.includes(e.value)) : all;
  const entries = filtered
    ? filtered.map((e) => ({ ...e, value: hideMicros(e.value) })) : filtered;
  const rendered = entries && entries.length
    ? <ValueList entries={entries} additions={additions} />
    : (!entries && text(value) ? <ClampedText>{hideMicros(text(value))}</ClampedText> : <span style={{ color: '#9ca3af' }}>—</span>);

  if (!dropped.length) return rendered;
  return (
    <div>
      {rendered}
      {/* Values the apply path refused. Shown next to the ones that WILL be sent, because
          a reader comparing the list against the line item would otherwise count entries
          that are never going to arrive. */}
      <div style={{ marginTop: '6px' }}>
        <div style={{ fontSize: '0.9em', color: '#991b1b', marginBottom: '2px' }}>
          not applied ({dropped.length}):
        </div>
        <ValueList entries={dropped} muted />
      </div>
    </div>
  );
}

/** Entity chips — "name (id)" per entity, wrapped. */
function EntityList({ entities }) {
  if (!entities.length) return <span style={{ color: '#9ca3af' }}>—</span>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {entities.map((entity, i) => (
        <div key={`${entity.id || entity.name}-${i}`}>
          {entity.name && <span style={{ marginRight: '5px' }}>{entity.name}</span>}
          {entity.id && <IdChip>{entity.id}</IdChip>}
        </div>
      ))}
    </div>
  );
}

const isGads = (platform) => {
  const value = String(platform || '').toLowerCase();
  return value === 'search' || value === 'pmax';
};

/**
 * Every per-insertion-order findings table a focused run can emit. Each is one row per
 * IO, deduplicated on the IO, and each is the authoritative source of that IO's NAME.
 */
const IO_TABLE_KEYS = [
  'insertion_order_pacing',        // pacing
  'insertion_order_performance',   // performance
  'insertion_order_status',        // diagnostic
];

/**
 * Insertion order names, by id, from whichever findings table this run emitted.
 *
 * The recommendation carries `insertion_order_id`, and a name only when the model
 * troubled to repeat it. The findings tables have both for every IO on the campaign, and
 * the pacing one is computed in code (campaign_optimizer/dv360_pacing_math.py), so a
 * name resolved from there is the IO's real name rather than one retyped from memory.
 */
export function ioNamesById(details) {
  const names = new Map();
  for (const key of IO_TABLE_KEYS) {
    const rows = Array.isArray(details?.[key]) ? details[key] : [];
    for (const row of rows) {
      const id = text(row?.insertion_order_id);
      const name = text(row?.insertion_order_name);
      if (id && name && !names.has(id)) names.set(id, name);
    }
  }
  return names;
}

/** The per-line-item findings rows, which carry the insertion order each line sits under. */
const LINE_ITEM_TABLE_KEYS = [
  'line_item_performance', 'delivery_diagnosis', 'shortfall_contributors',
  'excluded_from_pacing', 'stopped_delivery',
];

/**
 * line item id -> insertion order id, from the run's own per-line-item findings.
 *
 * The launcher resolves this from the DV360 objects and writes it onto every row, which
 * is where it should come from. This is the fallback for a result stored BEFORE it did:
 * a live performance run came back with three line item ids, no insertion order and no
 * names, and its scope column read "—" on every row. The per-line-item findings in that
 * same result name the insertion order, so the row can be placed without a re-run.
 */
export function ioByLineItem(details) {
  const map = new Map();
  for (const key of LINE_ITEM_TABLE_KEYS) {
    const rows = Array.isArray(details?.[key]) ? details[key] : [];
    for (const row of rows) {
      const lineItemId = text(row?.line_item_id);
      const ioId = text(row?.insertion_order_id);
      if (lineItemId && ioId && !map.has(lineItemId)) map.set(lineItemId, ioId);
    }
  }
  return map;
}

/**
 * The first sentence of a longer piece of prose.
 *
 * Notes and manual-apply reasons sit in narrow cells. A run that writes four sentences
 * gets its first one read and the rest hidden behind a link; taking the first sentence
 * makes that the visible unit rather than an arbitrary character count mid-word.
 */
export function firstSentence(value) {
  const raw = text(value);
  if (!raw) return null;
  const end = raw.search(/[.!?](\s|$)/);
  return end === -1 ? raw : raw.slice(0, end + 1);
}

/**
 * The two middle headings, per analysis.
 *
 * A diagnostic row does not propose a new VALUE — it reports a state and the job that
 * clears it — and heading that column "Recommended value" over the word "assign
 * creatives" reads as though a value went missing.
 */
const COLUMN_HEADINGS = {
  diagnostic: { current: 'What the data shows', recommended: 'Fix' },
};
const DEFAULT_HEADINGS = { current: 'Current value', recommended: 'Recommended value' };

/**
 * The badge under the scope cell. Each analysis leads with a different judgement:
 * pacing with the pacing status, the diagnostic with severity, performance with the
 * impact of acting. Showing whichever the row carries keeps one column doing the work
 * of three.
 */
function rowStatus(suggestion) {
  return text(suggestion.pacing_status)
    || text(suggestion.severity)
    || text(suggestion.expected_impact);
}

/**
 * One recommendation → the row's scope and targets.
 *
 * DV360 names an insertion order and a list of line items. Search and PMax name one
 * target at a level given by `target_type`, under the single campaign the run paced —
 * so the campaign fills the scope column and the target its own.
 */
export function resolveRow(suggestion, { gads, names, byLineItem, campaignLabel }) {
  if (gads) {
    const targetType = text(suggestion.target_type);
    const targetId = text(suggestion.target_id);
    const targetName = text(suggestion.target_name);
    const campaignScoped = !targetType || targetType.toLowerCase() === 'campaign';
    return {
      scope: { name: campaignLabel || 'Campaign', id: null },
      targets: campaignScoped
        ? [{ name: 'campaign-level', id: null }]
        : [{ name: targetName || targetType.replace(/_/g, ' '), id: targetId }],
    };
  }

  const listedIds = firstList(suggestion,
    'line_item_ids', 'affected_line_items', 'entity_ids', 'affected_ad_sets', 'affected_campaigns');
  const targetIds = listedIds.length
    ? listedIds
    : (text(suggestion.line_item_id) ? [text(suggestion.line_item_id)] : []);
  // The row's own insertion order, or the one its line items sit under. Only when they
  // AGREE: a row spanning two insertion orders belongs to neither, and picking the first
  // would file the change under a goal it half belongs to.
  const resolved = new Set(targetIds.map((id) => byLineItem?.get(id)).filter(Boolean));
  const ioId = text(suggestion.insertion_order_id)
    || (resolved.size === 1 ? [...resolved][0] : null);
  // One vocabulary, three analyses. Pacing and the reshaped performance / diagnostic
  // schemas all say line_item_ids; `affected_line_items` is what performance said before
  // and is still what a stored result carries, and `entity_ids` is the social launchers'
  // word for the same column.
  const listedNames = firstList(suggestion, 'line_item_names', 'entity_names');
  const allIds = targetIds;
  // A social run names the level its entities sit at; DV360's levels are fixed.
  const level = text(suggestion.level);

  return {
    scope: {
      // The model's own name is the fallback, not the source: the computed findings
      // table is authoritative for what an IO is called.
      name: (ioId && names.get(ioId)) || text(suggestion.insertion_order_name) || null,
      id: ioId,
    },
    targets: allIds.length
      ? allIds.map((id, i) => ({ id, name: listedNames[i] || null }))
      // A change scoped to the parent has no child entity, and saying so beats an empty
      // cell that reads as missing data.
      : [{ name: level ? `${level.replace(/_/g, ' ')} level` : 'insertion-order level', id: null }],
  };
}

/** The first of these keys holding a non-empty array, as text. */
function firstList(source, ...keys) {
  for (const key of keys) {
    const values = Array.isArray(source?.[key]) ? source[key].map(text).filter(Boolean) : [];
    if (values.length) return values;
  }
  return [];
}

/**
 * The notes cell: ONE sentence, the impact the run expects.
 *
 * It used to concatenate rationale, evidence, mechanism, plain_summary and description —
 * five fields that overlap heavily — which produced a paragraph in a cell that shows
 * about a line and a half. What the reader saw was an ellipsis and a "more" link. The
 * briefs now ask for one sentence carrying the expected impact; this keeps that promise
 * for the runs that write more, and the rest stays behind the toggle rather than being
 * thrown away.
 */
function noteFor(suggestion) {
  const why = suggestion.why && typeof suggestion.why === 'object' ? suggestion.why : null;
  return text(suggestion.rationale)
    || (why ? text(why.evidence) : null)
    || text(suggestion.plain_summary)
    || text(suggestion.description)
    || null;
}

/**
 * The Actual Impact cell for one row.
 *
 * Renders ONLY what the backend measured. Three states, and the difference
 * between them is the whole point of the column:
 *
 *   nothing    this row was never applied — an empty cell, not a zero
 *   pending    applied, but not yet readable (too recent, no baseline, no
 *              per-day reporting on this platform) — it says which
 *   measured   the before → after on delivery, CTR and conversion rate
 *
 * Delivery is coloured neutral on purpose: a change that cut a wasteful budget
 * is SUPPOSED to reduce impressions, and painting that red would report a
 * success as a failure. See utils/changeImpactPresentation.js.
 */
export function ImpactCell({ impact }) {
  const measured = impactCell(impact);
  if (!measured) return <span style={{ color: '#9ca3af' }}>—</span>;
  if (measured.kind === 'pending') {
    return <span style={{ color: '#6b7280', fontSize: '0.95em' }}>{measured.text}</span>;
  }
  return (
    <div title={impactText(impact)}>
      {measured.rows.map((row) => (
        <div key={row.key} style={{ display: 'flex', gap: '6px', alignItems: 'baseline', marginBottom: '2px' }}>
          <span style={{ color: '#6b7280', minWidth: '74px' }}>{row.label}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.before} → {row.after}
          </span>
          {row.pct && (
            <span style={{ color: directionColor(row.key, row.direction), fontWeight: 600 }}>
              {directionArrow(row.direction)} {row.pct}
            </span>
          )}
        </div>
      ))}
      {measured.windowDays && (
        <div style={{ color: '#9ca3af', fontSize: '0.9em', marginTop: '3px' }}>
          {measured.windowDays}d before vs {measured.windowDays}d after
        </div>
      )}
      {/* Not a footnote. Another change inside the same window means this
          before/after cannot be read as the effect of THIS row. */}
      {measured.confounders > 0 && (
        <div style={{ color: '#b45309', fontSize: '0.9em', marginTop: '3px' }}>
          {measured.confounders} other applied change
          {measured.confounders === 1 ? '' : 's'} in this window
        </div>
      )}
    </div>
  );
}

/**
 * One focused run's actionable rows, as a table.
 *
 * @param {Array}    items            resolved suggestions, in display order
 * @param {object}   details          the findings object the run's renderer reads
 * @param {string}   platform         result envelope's platform ('dv360' | 'search' | 'pmax')
 * @param {string}   kind             focused analysis kind ('pacing' | 'performance' | 'diagnostic')
 * @param {string}   campaignLabel    campaign name, for the Search / PMax scope column
 * @param {Function} renderAction     (suggestion, applyIndex) => the action cell's content
 * @param {Function} [impactFor]      (applyIndex) => the measured impact fact for an
 *   applied row, or null. Absent ⇒ the Actual Impact column is not rendered at all,
 *   so a caller that cannot measure does not show an entire column of dashes.
 */
export default function FocusedRecommendationsTable({
  items, details, platform, kind, campaignLabel, renderAction, impactFor = null,
}) {
  const rows = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!rows.length) return null;

  // Audits use the same comparison layout, but an advisory finding often has
  // no before/after values. Keep the observation and repair guidance visible.
  if (kind === 'audit') {
    return (
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
        <table aria-label="Campaign audit findings" style={{ borderCollapse: 'collapse', width: '100%', minWidth: '860px', background: '#fff' }}>
          <thead><tr>
            {['Target', 'Severity', 'Finding', 'Recommendation', 'Action'].map((label) => <th key={label} style={headerCell}>{label}</th>)}
          </tr></thead>
          <tbody>{rows.map((suggestion, index) => {
            const applyIndex = suggestion.__originalIndex ?? index;
            const ids = firstList(suggestion, 'target_entities', 'line_item_ids', 'entity_ids');
            if (!ids.length && text(suggestion.target_id)) ids.push(text(suggestion.target_id));
            const severity = text(suggestion.audit_severity) || text(suggestion.priority) || '—';
            return (
              <tr key={applyIndex}>
                <td style={{ ...cell, minWidth: '150px' }}>
                  <div style={{ fontWeight: 600 }}>{text(suggestion.title) || text(suggestion.target_name) || campaignLabel || 'Campaign'}</div>
                  {suggestion.target_type && <div>{text(suggestion.target_type).replace(/_/g, ' ')}</div>}
                  <EntityList entities={ids.map((id) => ({ id, name: null }))} />
                </td>
                <td style={cell}><Badge level={severity}>{severity}</Badge></td>
                <td style={{ ...cell, minWidth: '240px' }}>
                  <ClampedText>{text(suggestion.plain_summary) || text(suggestion.description)}</ClampedText>
                  {suggestion.current_value != null && <div style={{ marginTop: '8px' }}><strong>Current: </strong><ValueCell value={suggestion.current_value} /></div>}
                </td>
                <td style={{ ...cell, minWidth: '240px' }}>
                  <ClampedText>{text(suggestion.rationale)}</ClampedText>
                  {suggestion.recommended_value != null && <div style={{ marginTop: '8px' }}><strong>Recommended: </strong><ValueCell value={suggestion.recommended_value} additions={keywordAdditions(suggestion)} /></div>}
                </td>
                <td style={{ ...cell, minWidth: '160px' }}>{renderAction ? renderAction(suggestion, applyIndex) : null}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    );
  }

  // TTD also targets one ad group under a campaign, rather than an insertion order.
  const gads = isGads(platform) || String(platform || '').toLowerCase() === 'ttd';
  const names = gads ? new Map() : ioNamesById(details);
  // For rows that named their line items but not the insertion order they sit under.
  const byLineItem = gads ? new Map() : ioByLineItem(details);
  const scopeHeading = gads ? 'Campaign' : 'Insertion order';
  const targetHeading = gads ? 'Target' : 'Line items';
  const headings = COLUMN_HEADINGS[kind] || DEFAULT_HEADINGS;

  return (
    // The table scrolls inside its own container: six columns of ids and domain lists
    // will not fit a narrow screen, and a table that widens the page breaks the whole
    // activity feed rather than just itself.
    <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
      {/* The seventh column needs its own room; the container scrolls rather than
          widening the page, which would break the whole activity feed. */}
      <table style={{
        borderCollapse: 'collapse', width: '100%', background: '#fff',
        minWidth: impactFor ? '1090px' : '860px',
      }}>
        <thead>
          <tr>
            <th style={headerCell}>{scopeHeading}</th>
            <th style={headerCell}>{targetHeading}</th>
            <th style={headerCell}>{headings.current}</th>
            <th style={headerCell}>{headings.recommended}</th>
            <th style={{ ...headerCell, minWidth: '220px' }}>Reasoning notes</th>
            <th style={headerCell}>Action</th>
            {impactFor && <th style={{ ...headerCell, minWidth: '230px' }}>Actual Impact</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((suggestion, index) => {
            // Apply state is keyed on the BACKEND's original position, preserved through
            // display sorting — the same index the cards use, so a suggestion applied in
            // one layout shows as applied in the other.
            const applyIndex = suggestion.__originalIndex != null ? suggestion.__originalIndex : index;
            const { scope, targets } = resolveRow(suggestion, {
              gads, names, byLineItem, campaignLabel,
            });
            const status = rowStatus(suggestion);
            return (
              <tr key={`${text(suggestion.recommendation_id) || 'rec'}-${applyIndex}`}>
                <td style={cell}>
                  {scope.name && <div style={{ fontWeight: 600, marginBottom: '3px' }}>{scope.name}</div>}
                  {scope.id ? <IdChip>{scope.id}</IdChip>
                    : (!scope.name && <span style={{ color: '#9ca3af' }}>—</span>)}
                  {status && (
                    <div style={{ marginTop: '5px' }}>
                      <Badge level={status}>{status.replace(/_/g, ' ')}</Badge>
                    </div>
                  )}
                </td>
                <td style={cell}><EntityList entities={targets} /></td>
                <td style={cell}>
                  <ValueCell
                    value={suggestion.current_value}
                    names={firstList(suggestion, 'current_deal_names', 'current_audience_names')}
                  />
                </td>
                {/* The VALUE, and nothing above it. The action label used to sit here —
                    "Pause line item" printed over a value of "Paused", "Expand targeting"
                    over the list — which is the row restating itself in the one column
                    that exists to be compared against the current value. */}
                <td style={cell}>
                  <ValueCell
                    value={suggestion.recommended_value}
                    additions={keywordAdditions(suggestion)}
                    skipped={suggestion.skipped_values}
                    names={firstList(suggestion, 'deal_names', 'audience_names')}
                  />
                </td>
                <td style={cell}><ClampedText>{noteFor(suggestion)}</ClampedText></td>
                <td style={{ ...cell, whiteSpace: 'normal', minWidth: '150px' }}>
                  {renderAction ? renderAction(suggestion, applyIndex) : null}
                </td>
                {impactFor && (
                  <td style={{ ...cell, whiteSpace: 'normal', minWidth: '230px' }}>
                    <ImpactCell impact={impactFor(applyIndex)} />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
